import React, { useMemo, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import IconButton from '@material-ui/core/IconButton'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import InputBase from '@material-ui/core/InputBase'
import SearchIcon from '@material-ui/icons/Search'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    background: theme.palette.action.hover,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 500,
  },
}))

function formatName(p) {
  if (!p) return ''
  return [p.first_name || p.name || p.full_name, p.last_name].filter(Boolean).join(' ')
}

export default function CareProviderStaffList({ careProvider, profiles = [], onBack }) {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  const token = useSelector(state => state.auth && state.auth.token)

  const [serverStaff, setServerStaff] = useState([])
  const [loadingServerStaff, setLoadingServerStaff] = useState(false)
  const [serverError, setServerError] = useState(null)

  // Heuristic: staff entries may have a `care_provider` pointing to provider id and a `is_staff` or `staff_role` field
  // Prefer server-provided staff list for the selected care provider. If server returns empty, fall back to client-side heuristic using `profiles`.
  const staff = useMemo(() => {
    if (serverStaff && serverStaff.length) return serverStaff
    if (!careProvider) return []
    const id = careProvider.id || careProvider.pk || careProvider._id
    return (profiles || []).filter((p) => {
      // supports multiple profile shapes
      const cp = p.care_provider || (p.profile && p.profile.care_provider) || null
      const isStaff = p.is_staff || p.staff_role || p.role === 'staff' || p.isCareProviderStaff
      const matchesProvider = cp && (cp === id || (cp.id && String(cp.id) === String(id)) || (cp.pk && String(cp.pk) === String(id)))
      return matchesProvider && isStaff
    })
  }, [careProvider, profiles, serverStaff])

  // Fetch staff from backend when a careProvider is selected
  useEffect(() => {
    let cancelled = false
    const id = careProvider && (careProvider.id || careProvider.pk || careProvider._id)
    if (!id) {
      setServerStaff([])
      setServerError(null)
      setLoadingServerStaff(false)
      return
    }

    const fetchStaff = async () => {
      setLoadingServerStaff(true)
      setServerError(null)
      try {
        const base = 'https://api.virtus-one.com'
        // Primary expected endpoint: /profile/careprovider/<id>/staff/
        const urls = [
          `${base}/profile/careprovider/${id}/staff/`,
          `${base}/profile/staff/?care_provider_id=${id}`
        ]
        let result = null
        for (const url of urls) {
          const res = await fetch(url, { headers: { Authorization: token ? `Token ${token}` : '', Accept: 'application/json' } })
          if (!res) continue
          if (res.status === 404) continue
          if (!res.ok) throw new Error(`Failed to fetch staff: ${res.status}`)
          const data = await res.json()
          // API may return { staff: [...] } or an array directly
          result = Array.isArray(data) ? data : (data.staff || data.results || data.users || data)
          break
        }
        if (!cancelled) setServerStaff(result || [])
      } catch (err) {
        if (!cancelled) setServerError(err.message || String(err))
      } finally {
        if (!cancelled) setLoadingServerStaff(false)
      }
    }

    fetchStaff()
    return () => { cancelled = true }
  }, [careProvider, token])

  const filtered = staff.filter((p) => {
    const text = `${formatName(p)} ${p.email || ''} ${p.staff_role || ''}`.toLowerCase()
    return text.includes(query.trim().toLowerCase())
  })

  return (
    <Paper className={classes.container} elevation={1}>
      <div className={classes.header}>
        <IconButton onClick={onBack} aria-label="back" size="small">
          <ArrowBackIcon />
        </IconButton>
        <div style={{ flex: 1 }}>
          <div className={classes.title}>{careProvider.name || careProvider.display_name || 'Care Provider'}</div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>{careProvider.city || careProvider.address || ''}</div>
        </div>

        <div className={classes.search}>
          <SearchIcon fontSize="small" />
          <InputBase
            placeholder="Search staff"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ 'aria-label': 'search staff' }}
            style={{ marginLeft: 8 }}
          />
        </div>
      </div>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id || p.pk || p.email} hover>
                <TableCell>{formatName(p)}</TableCell>
                <TableCell>{p.email || ''}</TableCell>
                <TableCell>{p.staff_role || p.role || ''}</TableCell>
                <TableCell>{p.phone || p.mobile || ''}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} style={{ color: '#666' }}>
                  No staff found for this care provider.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
